import React from 'react';
import { Box, Switch, SwitchProps } from '@mui/material';
import { styled } from '@mui/material/styles';

type StatusIconInput = string | React.ReactNode;

interface MaStatusSwitchProps extends Omit<SwitchProps, 'icon' | 'checkedIcon'> {
  offIcon?: StatusIconInput;
  onIcon?: StatusIconInput;
  offThumbColor?: string;
  onThumbColor?: string;
  offIconColor?: string;
  onIconColor?: string;
  offTrackColor?: string;
  onTrackColor?: string;
}

interface StyledMaStatusSwitchProps {
  offTrackColor: string;
  onTrackColor: string;
}

const StyledMaStatusSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== 'offTrackColor' && prop !== 'onTrackColor',
})<StyledMaStatusSwitchProps>(({ offTrackColor, onTrackColor }) => ({
  width: 62,
  height: 34,
  padding: 7,
  overflow: 'visible',
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      transform: 'translateX(22px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: onTrackColor,
      },
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 1,
    },
  },
  '& .MuiSwitch-thumb': {
      width: 32,
      height: 32,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
      overflow: 'hidden',
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    borderRadius: 17,
    backgroundColor: offTrackColor,
  },
}));

const renderStatusIcon = (icon: StatusIconInput, color: string): React.ReactNode => {
  if (typeof icon === 'string') {
    return React.createElement(
      'span',
      {
        className: 'material-icons material-symbols-rounded',
        style: {
          color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          lineHeight: 1,
          width: '24px',
          height: '24px',
          minWidth: '24px',
          minHeight: '24px',
        },
      },
      icon
    );
  }

  return React.createElement(
    'span',
    {
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        lineHeight: 1,
      },
    },
    icon
  );
};

const StatusKnob: React.FC<{
  icon: StatusIconInput;
  thumbColor: string;
  iconColor: string;
}> = ({ icon, thumbColor, iconColor }) => {
  return React.createElement(
    Box,
    {
      sx: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: thumbColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'none',
        flexShrink: 0,
      },
    },
    renderStatusIcon(icon, iconColor)
  );
};

const MaStatusSwitch: React.FC<MaStatusSwitchProps> = ({
  offIcon = 'toggle_off',
  onIcon = 'toggle_on',
  offThumbColor = '#ffffff',
  onThumbColor = '#4caf50',
  offIconColor = '#9e9e9e',
  onIconColor = '#2e7d32',
  offTrackColor = '#aab4be',
  onTrackColor = '#aab4be',
  ...switchProps
}) => {
  return React.createElement(StyledMaStatusSwitch, {
    offTrackColor,
    onTrackColor,
    icon: React.createElement(StatusKnob, { icon: offIcon, thumbColor: offThumbColor, iconColor: offIconColor }),
    checkedIcon: React.createElement(StatusKnob, { icon: onIcon, thumbColor: onThumbColor, iconColor: onIconColor }),
    ...switchProps,
  });
};

export default MaStatusSwitch;
